import { useEffect, useMemo } from 'react';
import MobilePage from '../UiComponents/MobilePage';
import { legalDocuments } from './legalDocuments';

const stripComments = (markdown) => markdown.replace(/<!--[\s\S]*?-->/g, '').trim();

const isHeading = (line) => /^#{1,6}\s+/.test(line);
const isTableLine = (line) => line.trim().startsWith('|') && line.trim().endsWith('|');
const isTableSeparator = (line) => /^\s*\|?[\s:-]+\|[\s|:-]*$/.test(line);
const isListLine = (line) => /^(\s*)(\d+\.|-)\s+(.+)$/.test(line);

const parseTableRow = (line) => line
  .trim()
  .replace(/^\|/, '')
  .replace(/\|$/, '')
  .split('|')
  .map(cell => cell.trim());

const parseMarkdown = (markdown) => {
  const lines = stripComments(markdown).split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2],
      });
      i += 1;
      continue;
    }

    if (trimmed.startsWith('시행일:')) {
      blocks.push({ type: 'meta', text: trimmed });
      i += 1;
      continue;
    }

    if (isTableLine(line) && lines[i + 1] && isTableSeparator(lines[i + 1])) {
      const tableLines = [];
      while (i < lines.length && isTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }

      blocks.push({
        type: 'table',
        headers: parseTableRow(tableLines[0]),
        rows: tableLines.slice(2).map(parseTableRow),
      });
      continue;
    }

    if (isListLine(line)) {
      const items = [];
      while (i < lines.length && isListLine(lines[i])) {
        const itemMatch = lines[i].match(/^(\s*)(\d+\.|-)\s+(.+)$/);
        items.push({
          indent: itemMatch[1].length,
          marker: itemMatch[2],
          text: itemMatch[3],
        });
        i += 1;
      }
      blocks.push({ type: 'list', items });
      continue;
    }

    const paragraphLines = [];
    while (
      i < lines.length
      && lines[i].trim()
      && !isHeading(lines[i].trim())
      && !isListLine(lines[i])
      && !(isTableLine(lines[i]) && lines[i + 1] && isTableSeparator(lines[i + 1]))
    ) {
      paragraphLines.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
  }

  return blocks;
};

const linkify = (text) => {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);

  return parts.map((part, index) => {
    if (!/^https?:\/\//.test(part)) return part;

    return (
      <a
        key={`${part}-${index}`}
        href={part}
        target='_blank'
        rel='noreferrer'
        className='text-new-green underline underline-offset-4'
      >
        {part}
      </a>
    );
  });
};

const MarkdownBlock = ({ block }) => {
  if (block.type === 'heading') {
    if (block.level === 1) {
      return <h1 className='font-jua text-3xl leading-tight text-text-main'>{block.text}</h1>;
    }

    return (
      <h2 className='mt-10 border-t border-gray-200 pt-8 text-xl font-bold leading-snug text-text-main'>
        {block.text}
      </h2>
    );
  }

  if (block.type === 'meta') {
    return <p className='mt-4 text-sm font-bold text-new-green'>{block.text}</p>;
  }

  if (block.type === 'paragraph') {
    return <p className='mt-4 text-sm leading-7 text-text-main'>{linkify(block.text)}</p>;
  }

  if (block.type === 'list') {
    return (
      <div className='mt-4 space-y-2 text-sm leading-7 text-text-main'>
        {block.items.map((item, index) => (
          <div
            key={`${item.marker}-${item.text}-${index}`}
            className='flex gap-2'
            style={{ paddingLeft: `${Math.min(item.indent, 6) * 10}px` }}
          >
            <span className='min-w-[1.75rem] shrink-0 font-bold text-new-green'>{item.marker}</span>
            <span>{linkify(item.text)}</span>
          </div>
        ))}
      </div>
    );
  }

  if (block.type === 'table') {
    return (
      <div className='mt-5 overflow-x-auto border-y border-gray-200'>
        <table className='min-w-full border-collapse text-left text-xs leading-6 text-text-main'>
          <thead className='bg-background-floor text-text-main'>
            <tr>
              {block.headers.map((header, index) => (
                <th key={`${header}-${index}`} className='min-w-32 whitespace-nowrap border-b border-gray-200 px-3 py-3 font-bold'>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={`row-${rowIndex}`} className='border-t border-gray-100'>
                {row.map((cell, cellIndex) => (
                  <td key={`${cell}-${cellIndex}`} className='min-w-40 align-top px-3 py-3'>
                    {linkify(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
};

const LegalPage = ({ documentKey }) => {
  const legalDocument = legalDocuments[documentKey];
  const blocks = useMemo(() => parseMarkdown(legalDocument.markdown), [legalDocument.markdown]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
    window.document.title = `${legalDocument.title} | Telling Papa`;
  }, [legalDocument.title]);

  return (
    <MobilePage topCheckDisabled>
      <main className='min-h-screen bg-new-white px-5 pb-16 pt-24'>
        <article className='mx-auto max-w-md'>
          <div className='space-y-1'>
            {blocks.map((block, index) => (
              <MarkdownBlock key={`${block.type}-${index}`} block={block} />
            ))}
          </div>
        </article>
      </main>
    </MobilePage>
  );
};

export default LegalPage;
